import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Plus, Trash2, Save, FolderOpen, Upload, 
  Workflow, FileJson, Layers
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import PageTransition from "@/components/PageTransition";
import { staggerContainer, staggerItem, hoverScale, fadeInUp } from "@/config/animations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_STACKED = "/logo_dark.png";
const LOGO_LIGHT = "/logo_light.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

// Agent colors for visualization
const AGENT_COLORS = {
  "gpt-5.2": "#00A67E",
  "gpt-4o": "#10B981",
  "gpt-4o-mini": "#34D399",
  "claude-sonnet": "#E87B35",
  "claude-haiku": "#FCD34D",
  "gemini-3-flash": "#06B6D4",
  "gemini-3-pro": "#0EA5E9",
  "local": "#9CA3AF"
};

// Task Node Component
const TaskNode = ({ task, index, onUpdate, onDelete, agents, allTasks }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="card-dark rounded-lg p-4 border-2"
      style={{ borderColor: AGENT_COLORS[task.agent] || "var(--qn-border)" }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-[var(--qn-foreground-subtle)]">{task.id}</span>
          <Badge 
            className="text-sm"
            style={{ 
              backgroundColor: (AGENT_COLORS[task.agent] || "#374151") + "20", 
              color: AGENT_COLORS[task.agent] || "var(--qn-foreground-subtle)" 
            }}
          >
            {task.agent}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-red-400 hover:bg-red-500/10"
          onClick={() => onDelete(index)}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-3">
        <Input
          value={task.name}
          onChange={(e) => onUpdate(index, { ...task, name: e.target.value })}
          placeholder="Task name"
          className="bg-[var(--qn-background)] border-[var(--qn-border)] text-sm"
        />

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-sm text-[var(--qn-foreground-subtle)]">Agent</Label>
            <Select 
              value={task.agent} 
              onValueChange={(value) => onUpdate(index, { ...task, agent: value })}
            >
              <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-[var(--qn-foreground-subtle)]">Est. Tokens</Label>
            <Input
              type="number"
              value={task.estimated_tokens}
              onChange={(e) => onUpdate(index, { ...task, estimated_tokens: parseInt(e.target.value) || 0 })}
              className="bg-[var(--qn-background)] border-[var(--qn-border)] text-sm"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm text-[var(--qn-foreground-subtle)]">Dependencies</Label>
          <Select
            value={task.dependencies.length > 0 ? task.dependencies.join(",") : "none"}
            onValueChange={(value) => onUpdate(index, { 
              ...task, 
              dependencies: value === "none" ? [] : value.split(",").filter(Boolean) 
            })}
          >
            <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)] text-sm">
              <SelectValue placeholder="Select dependencies" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <SelectItem value="none">None</SelectItem>
              {allTasks
                .filter(t => t.id !== task.id)
                .map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.id}: {t.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </motion.div>
  );
};

export default function WorkflowBuilder() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { isDark } = useTheme();
  const [workflowName, setWorkflowName] = useState("New Workflow");
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [mcpConfig, setMcpConfig] = useState("");

  useEffect(() => {
    fetchAgents();
    fetchTemplates();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/workflow-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const addTask = () => {
    const newId = `t${tasks.length + 1}`;
    setTasks([...tasks, {
      id: newId,
      name: "New Task",
      agent: "gpt-4o",
      estimated_tokens: 1000,
      dependencies: tasks.length > 0 ? [tasks[tasks.length - 1].id] : [],
      priority: 1
    }]);
  };

  const updateTask = (index, updatedTask) => {
    const newTasks = [...tasks];
    newTasks[index] = updatedTask;
    setTasks(newTasks);
  };

  const deleteTask = (index) => {
    const deletedId = tasks[index].id;
    const newTasks = tasks.filter((_, i) => i !== index);
    // Remove deleted task from dependencies
    newTasks.forEach(task => {
      task.dependencies = task.dependencies.filter(dep => dep !== deletedId);
    });
    setTasks(newTasks);
  };

  const loadTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/workflows/from-template/${templateId}`);
      setWorkflowName(response.data.name);
      setTasks(response.data.tasks);
      toast.success("Template loaded!");
    } catch (error) {
      toast.error("Failed to load template");
    }
  };

  const importMCPConfig = async () => {
    try {
      const config = JSON.parse(mcpConfig);
      const response = await axios.post(`${API}/workflows/import/mcp`, {
        mcp_config: config,
        name: workflowName
      });
      setTasks(response.data.tasks);
      setImportDialogOpen(false);
      setMcpConfig("");
      toast.success("MCP workflow imported!");
    } catch (error) {
      toast.error("Invalid MCP configuration");
    }
  };

  const saveWorkflow = async () => {
    if (tasks.length === 0) {
      toast.error("Add at least one task");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/workflows`, {
        name: workflowName,
        tasks: tasks
      });
      toast.success("Workflow saved!");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  // Calculate estimated cost and time
  const estimatedCost = tasks.reduce((total, task) => {
    const agent = agents.find(a => a.id === task.agent);
    return total + (task.estimated_tokens * (agent?.cost_per_1k_tokens || 0.005) / 1000);
  }, 0);

  const estimatedTime = tasks.reduce((total, task) => {
    const agent = agents.find(a => a.id === task.agent);
    return total + (task.estimated_tokens / 1000) * (agent?.speed_factor || 1);
  }, 0);

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)]">
        {/* Header */}
        <motion.header 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass border-b border-[var(--qn-border)]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.div {...hoverScale}>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate("/dashboard")}
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </motion.div>
              <img src={LOGO_ICON} alt="QNNEAL" className={`h-9 w-9 logo-spin`} />
            </div>
            
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Input
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                className="w-64 bg-[var(--qn-background)] border-[var(--qn-border)]"
                placeholder="Workflow name"
                data-testid="workflow-name-input"
              />
              <motion.div {...hoverScale}>
                <Button 
                  onClick={saveWorkflow}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-workflow-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Workflow"}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid lg:grid-cols-3 gap-8"
          >
            {/* Left Panel - Templates & Import */}
            <motion.div variants={staggerItem} className="space-y-6">
              {/* Templates */}
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[var(--qn-primary)]" />
                    Templates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {templates.map((template) => (
                    <motion.div key={template.template_id} {...hoverScale}>
                      <Button
                        variant="outline"
                        className="w-full justify-start border-[var(--qn-border)] hover:border-[var(--qn-primary)]/50"
                        onClick={() => loadTemplate(template.template_id)}
                        data-testid={`template-${template.template_id}`}
                      >
                        <FolderOpen className="w-4 h-4 mr-2 text-[var(--qn-primary)]" />
                        <div className="text-left">
                          <div className="text-sm">{template.name}</div>
                          <div className="text-sm text-[var(--qn-foreground-subtle)]">{template.tasks.length} tasks</div>
                        </div>
                      </Button>
                    </motion.div>
                  ))}
                </CardContent>
              </Card>

              {/* Import MCP */}
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <Upload className="w-5 h-5 text-[var(--qn-secondary)]" />
                    Import
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full border-[var(--qn-border)]"
                        data-testid="import-mcp-btn"
                      >
                        <FileJson className="w-4 h-4 mr-2" />
                        Import MCP Config
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                      <DialogHeader>
                        <DialogTitle>Import MCP Configuration</DialogTitle>
                        <DialogDescription className="text-[var(--qn-foreground-muted)]">
                          Paste your MCP configuration JSON to import servers as workflow tasks
                        </DialogDescription>
                      </DialogHeader>
                      <Textarea
                        value={mcpConfig}
                        onChange={(e) => setMcpConfig(e.target.value)}
                        placeholder='{"mcpServers": { "server1": {...} }}'
                        className="min-h-[200px] bg-[var(--qn-background)] border-[var(--qn-border)] font-mono text-sm"
                      />
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={importMCPConfig} className="btn-primary">
                          Import
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              {/* Estimates */}
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg">Estimates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-muted)]">Tasks</span>
                    <span className="font-mono text-[var(--qn-primary)]">{tasks.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-muted)]">Est. Cost</span>
                    <span className="font-mono text-[var(--qn-success)]">${estimatedCost.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-muted)]">Est. Time</span>
                    <span className="font-mono text-[var(--qn-secondary)]">{estimatedTime.toFixed(2)}s</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Main Panel - Task Builder */}
            <motion.div variants={staggerItem} className="lg:col-span-2">
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-outfit flex items-center gap-2">
                      <Workflow className="w-5 h-5 text-[var(--qn-primary)]" />
                      Workflow Tasks
                    </CardTitle>
                    <CardDescription className="text-[var(--qn-foreground-muted)]">
                      Add and configure tasks for your workflow DAG
                    </CardDescription>
                  </div>
                  <motion.div {...hoverScale}>
                    <Button onClick={addTask} className="btn-primary" data-testid="add-task-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Task
                    </Button>
                  </motion.div>
                </CardHeader>
                <CardContent>
                  {tasks.length === 0 ? (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-16 text-[var(--qn-foreground-subtle)]"
                    >
                      <Workflow className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No tasks yet. Add a task or load a template to get started.</p>
                    </motion.div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      <AnimatePresence>
                        {tasks.map((task, index) => (
                          <TaskNode
                            key={task.id}
                            task={task}
                            index={index}
                            onUpdate={updateTask}
                            onDelete={deleteTask}
                            agents={agents}
                            allTasks={tasks}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </PageTransition>
  );
}
